import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaGavel, FaSearch, FaFilter, FaPhone, FaEnvelope,
  FaMapMarkerAlt, FaGlobe, FaExternalLinkAlt, FaPlus
} from 'react-icons/fa';
import './Courts.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const Courts = () => {
  const [courts, setCourts] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    counties: [],
    jurisdictions: [],
    judicial_districts: []
  });
  const [filters, setFilters] = useState({
    search: '',
    county: '',
    jurisdiction: '',
    judicial_district: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFilterOptions();
    fetchCourts();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCourts();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/courts/filters/options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setFilterOptions(data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.county) params.append('county', filters.county);
      if (filters.jurisdiction) params.append('jurisdiction', filters.jurisdiction);
      if (filters.judicial_district) params.append('judicial_district', filters.judicial_district);

      const response = await fetch(`${API_URL}/courts?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setCourts(data.data);
        setError('');
      } else {
        setError('Failed to load courts');
      }
    } catch (err) {
      console.error('Error fetching courts:', err);
      setError('Failed to load courts');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      county: '',
      jurisdiction: '',
      judicial_district: ''
    });
  };

  const handleCourtClick = (courtId) => {
    navigate(`/address-book/courts/${courtId}`);
  };

  const getJurisdictionBadge = (jurisdiction) => {
    const badges = {
      'district': { label: 'District Court', color: '#2c3e50' },
      'justice': { label: 'Justice Court', color: '#16a085' },
      'municipal': { label: 'Municipal Court', color: '#2980b9' },
      'city': { label: 'City Court', color: '#8e44ad' },
      'other': { label: 'Other', color: '#7f8c8d' }
    };

    const badge = badges[jurisdiction] || badges.other;

    return (
      <span className="jurisdiction-badge" style={{ backgroundColor: badge.color }}>
        {badge.label}
      </span>
    );
  };

  if (loading && courts.length === 0) {
    return (
      <div className="courts-container">
        <div className="loading">Loading courts...</div>
      </div>
    );
  }

  return (
    <div className="courts-container">
      {/* Header */}
      <div className="courts-header">
        <div className="header-left">
          <FaGavel size={32} />
          <div>
            <h1>Montana Courts</h1>
            <p className="subtitle">{courts.length} courts found</p>
          </div>
        </div>
        <button className="btn-add-court" onClick={() => navigate('/address-book/courts/new')}>
          <FaPlus /> Add Court
        </button>
      </div>

      {/* Filters */}
      <div className="courts-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by court name or city..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>County</label>
            <select
              value={filters.county}
              onChange={(e) => handleFilterChange('county', e.target.value)}
              className="filter-select"
            >
              <option value="">All Counties</option>
              {filterOptions.counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Jurisdiction</label>
            <select
              value={filters.jurisdiction}
              onChange={(e) => handleFilterChange('jurisdiction', e.target.value)}
              className="filter-select"
            >
              <option value="">All Jurisdictions</option>
              {filterOptions.jurisdictions.map(jurisdiction => (
                <option key={jurisdiction} value={jurisdiction}>
                  {jurisdiction.charAt(0).toUpperCase() + jurisdiction.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Judicial District</label>
            <select
              value={filters.judicial_district}
              onChange={(e) => handleFilterChange('judicial_district', e.target.value)}
              className="filter-select"
            >
              <option value="">All Districts</option>
              {filterOptions.judicial_districts.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
          </div>

          {(filters.search || filters.county || filters.jurisdiction || filters.judicial_district) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Courts List */}
      <div className="courts-list">
        {courts.length === 0 ? (
          <div className="no-results">
            <FaGavel size={48} />
            <p>No courts found matching your criteria</p>
          </div>
        ) : (
          courts.map(court => (
            <div
              key={court.id}
              className="court-card"
              onClick={() => handleCourtClick(court.id)}
            >
              <div className="court-header">
                <div className="court-title">
                  <h3>{court.name}</h3>
                  {getJurisdictionBadge(court.jurisdiction)}
                </div>
                {court.court_identifier && (
                  <div className="court-identifier">
                    ID: {court.court_identifier}
                  </div>
                )}
              </div>

              <div className="court-info">
                {court.judicial_district && (
                  <div className="info-item">
                    <strong>{court.judicial_district}</strong>
                  </div>
                )}

                <div className="info-row">
                  {court.county && (
                    <div className="info-item">
                      <FaMapMarkerAlt className="info-icon" />
                      <span>{court.city ? `${court.city}, ` : ''}{court.county}</span>
                    </div>
                  )}

                  {court.phone && (
                    <div className="info-item">
                      <FaPhone className="info-icon" />
                      <span>{court.phone}</span>
                    </div>
                  )}

                  {court.email && (
                    <div className="info-item">
                      <FaEnvelope className="info-icon" />
                      <span>{court.email}</span>
                    </div>
                  )}

                  {court.website && (
                    <div className="info-item">
                      <FaGlobe className="info-icon" />
                      <a
                        href={court.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="website-link"
                      >
                        Website <FaExternalLinkAlt size={10} />
                      </a>
                    </div>
                  )}
                </div>

                {(court.judge_count > 0 || court.clerk_count > 0) && (
                  <div className="court-stats">
                    {court.judge_count > 0 && (
                      <span className="stat-badge">{court.judge_count} Judge{court.judge_count !== 1 ? 's' : ''}</span>
                    )}
                    {court.clerk_count > 0 && (
                      <span className="stat-badge">{court.clerk_count} Clerk{court.clerk_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Courts;
